import {
  SelectRegionRequest,
  SelectRegionResponse,
  SelectRegionResponsePayload,
  UserRegion,
  UserRegionsResponsePayload,
} from './Lobby.types';

export interface LobbyHttpClient {
  get<T>(
    url: string,
    config?: { headers?: Record<string, string> },
  ): Promise<{ data: T }>;
  post<T>(
    url: string,
    body: unknown,
    config?: { headers?: Record<string, string> },
  ): Promise<{ data: T }>;
}

export class LobbyApi {
  constructor(private readonly client: LobbyHttpClient) {}

  /**
   * Gets regions assigned to the authenticated user via the lobby/login edge.
   */
  async getUserRegions(accessToken: string): Promise<UserRegion[]> {
    const token = accessToken.trim();
    if (!token) {
      throw new Error('accessToken is required.');
    }

    const response = await this.client.get<UserRegionsResponsePayload>('/me/regions', {
      headers: { Authorization: `Bearer ${token}` },
    });

    return this.normalizeRegions(response.data);
  }

  /**
   * Selects an active region for the authenticated user.
   */
  async selectRegion(regionId: string, accessToken: string): Promise<SelectRegionResponse> {
    const normalizedRegionId = regionId.trim();
    if (!normalizedRegionId) {
      throw new Error('regionId is required.');
    }

    const token = accessToken.trim();
    if (!token) {
      throw new Error('accessToken is required.');
    }

    const payload: SelectRegionRequest = { regionId: normalizedRegionId };
    const response = await this.client.post<SelectRegionResponsePayload>('/select-region', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const regionUrl =
      response.data.regionBaseUrl ?? response.data.regionUrl ?? response.data.baseUrl ?? response.data.url;

    if (!regionUrl) {
      throw new Error('select-region response did not include a region URL.');
    }

    const normalizedRegionUrl = regionUrl.replace(/\/$/, '');

    return {
      regionId: response.data.regionId ?? normalizedRegionId,
      regionUrl: normalizedRegionUrl,
      regionBaseUrl: normalizedRegionUrl,
    };
  }

  private normalizeRegions(payload: UserRegionsResponsePayload): UserRegion[] {
    const rawRegions = Array.isArray(payload)
      ? payload
      : payload.regions ?? payload.items ?? payload.data ?? [];

    return rawRegions
      .filter((region) => !!region.regionId && !!region.regionUrl)
      .map((region) => ({
        regionId: region.regionId,
        regionName: region.regionName,
        regionUrl: region.regionUrl.replace(/\/$/, ''),
      }));
  }
}
